import { useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import { ArrowRight, Ban, PackageOpen, Search, User } from 'lucide-react-native';

import { Button, Card, EmptyState, Sheet, SkeletonRows, TextField } from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { salidasQuery } from '@/services/firebase/salidas';
import { anularSalida } from '@/services/firebase/movimientosStock';
import { useAuth } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatDate } from '@/utils/format';
import { AREAS } from '@/utils/areas';

export function SalidasScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const { data: salidas, loading } = useFirestoreCollection(salidasQuery);

  const [areaFiltro, setAreaFiltro] = useState<string>('');
  const [coleccionFiltro, setColeccionFiltro] = useState<'todos' | 'productos_venta' | 'insumos_internos'>('todos');
  const [desdeStr, setDesdeStr] = useState('');
  const [hastaStr, setHastaStr] = useState('');
  const [search, setSearch] = useState('');
  const perfil = useAuth((s) => s.perfil);

  const [anularTarget, setAnularTarget] = useState<any | null>(null);
  const [motivo, setMotivo] = useState('');
  const [anulando, setAnulando] = useState(false);
  const [anularError, setAnularError] = useState<string | null>(null);

  const confirmarAnular = async () => {
    if (!anularTarget || !perfil) return;
    if (!motivo.trim()) { setAnularError('Poné el motivo de la anulación.'); return; }
    setAnulando(true);
    setAnularError(null);
    try {
      await anularSalida({ salidaId: anularTarget.id, motivo: motivo.trim(), autorUid: perfil.uid, autorNombre: perfil.nombre });
      setAnularTarget(null);
      setMotivo('');
    } catch (e: any) {
      setAnularError(e?.message ?? 'No se pudo anular.');
    } finally {
      setAnulando(false);
    }
  };

  const filtradas = useMemo(() => {
    const desde = desdeStr ? new Date(desdeStr) : null;
    const hasta = hastaStr
      ? (() => {
          const h = new Date(hastaStr);
          h.setHours(23, 59, 59, 999);
          return h;
        })()
      : null;
    const q = search.trim().toLowerCase();
    return salidas.filter((s) => {
      if (areaFiltro && s.destino !== areaFiltro) return false;
      if (coleccionFiltro !== 'todos' && s.coleccion !== coleccionFiltro) return false;
      const f = s.fecha?.toDate?.();
      if (desde && f && f < desde) return false;
      if (hasta && f && f > hasta) return false;
      if (q && !s.productoNombre.toLowerCase().includes(q) && !(s.motivo ?? '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [salidas, areaFiltro, coleccionFiltro, desdeStr, hastaStr, search]);

  const totalCantidad = filtradas.reduce((s, x) => s + (x.anulada ? 0 : x.cantidad), 0);

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 28 : 16, gap: 16, paddingBottom: 80 }}>
      {/* Hero */}
      <View
        style={{
          padding: wide ? 20 : 16,
          borderRadius: radius['3xl'],
          backgroundColor: colors.magenta[700],
          // @ts-ignore web only
          backgroundImage: `linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
        }}
      >
        <Text style={{ color: 'rgba(255,255,255,0.85)', fontSize: 11, fontWeight: typography.weights.bold, letterSpacing: 1.4, textTransform: 'uppercase' }}>
          Salidas de almacén · {filtradas.length} {filtradas.length === 1 ? 'movimiento' : 'movimientos'}
        </Text>
        <Text style={{ color: colors.white, fontFamily: typography.display.fontFamily, fontSize: wide ? 32 : 24, fontWeight: typography.weights.bold, marginTop: 2 }}>
          {totalCantidad} unidades retiradas
        </Text>
        <Text style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, marginTop: 2 }}>
          Filtrá por área, tipo, fechas o producto.
        </Text>
      </View>

      {/* Filtros */}
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <View style={{ flex: 1, minWidth: 240, gap: 4 }}>
            <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
              Buscar producto / motivo
            </Text>
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 8,
                backgroundColor: colors.crema[50],
                borderRadius: radius.full,
                paddingHorizontal: 14,
                height: 42,
                borderWidth: 1,
                borderColor: colors.ink[100],
              }}
            >
              <Search size={14} color={colors.ink[400]} />
              {Platform.OS === 'web' ? (
                // @ts-ignore web-only native input
                <input
                  type="text"
                  value={search}
                  onChange={(e: any) => setSearch(e.target.value)}
                  placeholder="Amoxican, vacuna, Mishi…"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    border: 'none',
                    outline: 'none',
                    fontSize: 14,
                    color: colors.ink[900],
                    fontFamily: 'inherit',
                  }}
                />
              ) : null}
            </View>
          </View>

          {Platform.OS === 'web' ? (
            <>
              <SelectCompacto
                label="Tipo"
                value={coleccionFiltro}
                onChange={(v) => setColeccionFiltro(v as any)}
                options={[
                  { value: 'todos', label: 'Todos' },
                  { value: 'productos_venta', label: 'Farmacia' },
                  { value: 'insumos_internos', label: 'Insumos' },
                ]}
              />
              <SelectCompacto
                label="Área"
                value={areaFiltro}
                onChange={setAreaFiltro}
                options={[
                  { value: '', label: 'Todas' },
                  ...AREAS.filter((a) => a.id !== 'otra').map((a) => ({ value: a.id, label: a.nombre })),
                ]}
              />
              <DateField label="Desde" value={desdeStr} onChange={setDesdeStr} />
              <DateField label="Hasta" value={hastaStr} onChange={setHastaStr} />
            </>
          ) : null}

          {(areaFiltro || coleccionFiltro !== 'todos' || desdeStr || hastaStr || search) ? (
            <Pressable
              onPress={() => {
                setAreaFiltro('');
                setColeccionFiltro('todos');
                setDesdeStr('');
                setHastaStr('');
                setSearch('');
              }}
              style={({ hovered }) => ({
                paddingHorizontal: 14,
                height: 42,
                borderRadius: radius.full,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? colors.crema[100] : colors.crema[50],
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.ink[700] }}>
                Limpiar
              </Text>
            </Pressable>
          ) : null}
        </View>
      </Card>

      {/* Lista */}
      {loading ? (
        <SkeletonRows rows={4} />
      ) : filtradas.length === 0 ? (
        <Card>
          <EmptyState
            illustration="note"
            title="Sin salidas registradas"
            message={salidas.length === 0 ? 'Cuando se registre una salida de almacén la vas a ver acá.' : 'No hay resultados con esos filtros.'}
          />
        </Card>
      ) : (
        <Card padding={0} style={{ overflow: 'hidden' }}>
          <View
            style={{
              flexDirection: 'row',
              paddingVertical: 12,
              backgroundColor: colors.crema[50],
              borderBottomWidth: 1,
              borderBottomColor: colors.ink[100],
            }}
          >
            <ColH text="Fecha" width={120} align="center" />
            <ColH text="Producto" flex={1.4} />
            <ColH text="Cantidad" width={90} align="center" />
            <ColH text="Destino" width={150} />
            <ColH text="Motivo" flex={1.2} />
            <ColH text="Quién" width={130} />
            <ColH text="" width={48} align="center" last />
          </View>
          {filtradas.map((s, idx) => (
            <View
              key={s.id}
              style={{
                flexDirection: 'row',
                alignItems: 'stretch',
                paddingVertical: 12,
                backgroundColor: s.anulada ? 'rgba(254,242,242,0.55)' : idx % 2 === 1 ? 'rgba(250,247,244,0.5)' : colors.white,
                borderBottomWidth: idx === filtradas.length - 1 ? 0 : 1,
                borderBottomColor: colors.ink[100],
                opacity: s.anulada ? 0.7 : 1,
              }}
            >
              <CellRow width={120} align="center">
                <Text style={{ fontSize: 12, color: colors.ink[800], textAlign: 'center' }}>
                  {formatDate(s.fecha.toDate())}
                </Text>
                <Text style={{ fontSize: 10, color: colors.ink[500], textAlign: 'center', fontFamily: 'monospace', marginTop: 2 }}>
                  {String(s.fecha.toDate().getHours()).padStart(2, '0')}:
                  {String(s.fecha.toDate().getMinutes()).padStart(2, '0')}
                </Text>
              </CellRow>
              <CellRow flex={1.4}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  <View style={{ width: 28, height: 28, borderRadius: 8, backgroundColor: s.coleccion === 'productos_venta' ? colors.magenta[100] : colors.success[50], alignItems: 'center', justifyContent: 'center' }}>
                    <PackageOpen size={14} color={s.coleccion === 'productos_venta' ? colors.magenta[700] : colors.success[600]} />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.ink[900] }} numberOfLines={1}>
                      {s.productoNombre}
                    </Text>
                    {s.productoPresentacion ? (
                      <Text style={{ fontSize: 11, color: colors.ink[500] }} numberOfLines={1}>
                        {s.productoPresentacion} · {s.coleccion === 'productos_venta' ? 'Farmacia' : 'Insumos'}
                      </Text>
                    ) : null}
                  </View>
                </View>
              </CellRow>
              <CellRow width={90} align="center">
                <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 18, fontWeight: typography.weights.bold, color: colors.magenta[700], textAlign: 'center' }}>
                  ×{s.cantidad}
                </Text>
              </CellRow>
              <CellRow width={150}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <ArrowRight size={12} color={colors.ink[500]} />
                  <Text style={{ fontSize: 12, color: colors.ink[800], fontWeight: typography.weights.semibold }} numberOfLines={1}>
                    {s.destinoNombre}
                  </Text>
                </View>
              </CellRow>
              <CellRow flex={1.2}>
                <Text style={{ fontSize: 12, color: colors.ink[600], fontStyle: 'italic' }} numberOfLines={2}>
                  {s.motivo || '—'}
                </Text>
              </CellRow>
              <CellRow width={130}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <User size={12} color={colors.ink[500]} />
                  <Text style={{ fontSize: 12, color: colors.ink[700] }} numberOfLines={1}>
                    {s.autorNombre}
                  </Text>
                </View>
                {s.anulada ? (
                  <Text style={{ fontSize: 10, color: colors.danger[600], fontWeight: typography.weights.bold, marginTop: 2 }} numberOfLines={1}>
                    ANULADA{s.motivoAnulacion ? ` · ${s.motivoAnulacion}` : ''}
                  </Text>
                ) : null}
              </CellRow>
              <CellRow width={48} align="center" last>
                {!s.anulada ? (
                  <Pressable
                    onPress={() => { setAnularTarget(s); setMotivo(''); setAnularError(null); }}
                    hitSlop={6}
                    title="Anular salida"
                    style={({ hovered }) => ({
                      width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center',
                      backgroundColor: hovered ? colors.danger[50] : 'transparent',
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <Ban size={14} color={colors.danger[600]} />
                  </Pressable>
                ) : null}
              </CellRow>
            </View>
          ))}
        </Card>
      )}

      {/* Anular salida/uso: devuelve el stock + queda registrada */}
      <Sheet
        visible={!!anularTarget}
        onClose={() => { if (!anulando) { setAnularTarget(null); setMotivo(''); setAnularError(null); } }}
        size="sm"
        title="Anular salida"
        subtitle={anularTarget ? `${anularTarget.productoNombre} ×${anularTarget.cantidad}` : undefined}
        footer={
          <>
            <Button label="Cancelar" variant="ghost" onPress={() => { setAnularTarget(null); setMotivo(''); setAnularError(null); }} disabled={anulando} />
            <Button label="Anular salida" variant="danger" iconLeft={<Ban size={15} color={colors.white} />} onPress={confirmarAnular} loading={anulando} />
          </>
        }
      >
        <View style={{ gap: 12 }}>
          <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.warning[50] }}>
            <Text style={{ fontSize: 13, color: colors.ink[700] }}>
              Se va a devolver {anularTarget ? anularTarget.cantidad : ''} al stock. La salida queda registrada como anulada (no se borra).
            </Text>
          </View>
          <TextField label="Motivo de la anulación" placeholder="Ej: se registró por error" value={motivo} onChangeText={setMotivo} autoFocus />
          {anularError ? (
            <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
              <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{anularError}</Text>
            </View>
          ) : null}
        </View>
      </Sheet>
    </ScrollView>
  );
}

function ColH({ text, flex, width, align = 'left', last }: { text: string; flex?: number; width?: number; align?: 'left' | 'right' | 'center'; last?: boolean }) {
  return (
    <View
      style={{
        flex,
        width,
        paddingHorizontal: 12,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[100],
      }}
    >
      <Text
        style={{
          fontSize: 11,
          color: colors.ink[500],
          fontWeight: typography.weights.bold,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
        }}
      >
        {text}
      </Text>
    </View>
  );
}

function CellRow({ children, flex, width, align = 'left', last }: { children: React.ReactNode; flex?: number; width?: number; align?: 'left' | 'right' | 'center'; last?: boolean }) {
  return (
    <View
      style={{
        flex,
        width,
        paddingHorizontal: 12,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'stretch',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: colors.ink[100],
      }}
    >
      {children}
    </View>
  );
}

function SelectCompacto({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <View style={{ gap: 4 }}>
      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </Text>
      {/* @ts-ignore web-only native select */}
      <select
        value={value}
        onChange={(e) => onChange((e.target as any).value)}
        style={{
          height: 42,
          padding: '0 12px',
          borderRadius: 999,
          border: `1px solid ${colors.ink[200]}`,
          background: colors.crema[50],
          color: colors.ink[900],
          fontSize: 13,
          fontFamily: 'inherit',
          outline: 'none',
          minWidth: 140,
        }}
      >
        {options.map((o) => (
          // @ts-ignore web-only option
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </View>
  );
}

function DateField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <View style={{ gap: 4 }}>
      <Text style={{ fontSize: 11, fontWeight: typography.weights.bold, color: colors.ink[500], letterSpacing: 0.6, textTransform: 'uppercase' }}>
        {label}
      </Text>
      {/* @ts-ignore web-only native input */}
      <input
        type="date"
        value={value}
        onChange={(e) => onChange((e.target as any).value)}
        style={{
          height: 42,
          padding: '0 12px',
          borderRadius: 12,
          border: `1px solid ${colors.ink[200]}`,
          background: colors.white,
          color: colors.ink[900],
          fontSize: 13,
          fontFamily: 'inherit',
          outline: 'none',
          width: 140,
          boxSizing: 'border-box',
        }}
      />
    </View>
  );
}
